/*
 * OnDevice.js
 * Registry of "on-device" payment solutions: the payment app runs on the same Android device as this
 * page and talks to it through ReactNativeWebView (Verifone, PAX, ...). Everything that differs between
 * the solutions lives in ONE place here, so a page never asks "is it Verifone or PAX?" itself.
 *
 *   OnDevice.active()                 this device is an on-device terminal (any provider)
 *   OnDevice.current()                its provider definition (or null)
 *   OnDevice.key()                    'verifone' | 'pax' | ... : the `action` the native app listens to
 *   OnDevice.isPaymentMethod(pm)      'payment_verifone_terminal' | 'payment_pax_terminal' | ...
 *   OnDevice.paymentMethods()         all of them
 *   OnDevice.byKey(k) / byPaymentMethod(pm) / byPrinterType(t) / fromDeviceInfo(info)   -> provider or null
 *   OnDevice.methodSelector(prefix)   '<prefix>[data-payment-method="a"], <prefix>[data-payment-method="b"]'
 *   OnDevice.uniqueId()               id of THIS device's terminal (localStorage 'temrinalUniqueId')
 *   OnDevice.sessionStarted()         payment session of the app is open (body attr is-verifone-session-started)
 *   OnDevice.send(message)            raw message to the native app
 *   OnDevice.command(action, data)    { action: <key>, data: { action, data } } to the native app
 *   OnDevice.start(message, {txId}?)  start a payment / refund in the app and record it (OnDeviceTransaction.js)
 *   OnDevice.checkState()             asks the app for its state (logged-in on-device pages only)
 *   OnDevice.printMessage(html, segments, providerKey?)   the 'print' message for the native app
 *   OnDevice.print(html, segments, providerKey?)          ... and sends it
 *
 * ---- Adding a new on-device solution --------------------------------------------------------------------
 *   1. PHP: a Terminal::*_TERMINAL type + its entries in Terminal::TERMINAL_TYPES_FOR_PRINTER /
 *      TERMINAL_TYPES_FOR_PAYMENT_METHOD, a UA check in index.php (and public/api, public/ajax) that sets
 *      $_SESSION['is_<key>'] and the body attribute is_<key>, and the entry in OnDeviceTerminal.php.
 *   2. JS: one OnDevice.register({...}) call below (key, bodyAttr, paymentMethod, printerType, terminalType).
 *   3. The websocket needs nothing: terminals are identified by their unique id, not by vendor
 *      (SocketHub 'terminal' role, see src/websocket/README.md).
 *   4. The native app answers 'verifone_state' / 'device_info' with its own flags (isVerifone, isPax, ...);
 *      ReactNativeMessageHandler.js shows where a provider-specific field (like PAX poiId) is read.
 */
(function (window, $) {
  'use strict';

  var UNIQUE_ID_KEY = 'temrinalUniqueId'; // sic: the apps already store it under this name
  var providers = {};
  var order = [];

  function register(def) {
    if (!def || !def.key) throw new Error('OnDevice.register: key is required');
    var p = $.extend(
      {
        key: '',
        bodyAttr: 'is_' + def.key, // <body is_pax="1">
        paymentMethod: 'payment_' + def.key + '_terminal',
        printerType: String(def.key).toUpperCase(), // Printer::PRINTER_TYPE_* / Terminal::TERMINAL_TYPES_FOR_PRINTER
        terminalType: null, // Terminal::*_TERMINAL
        deviceFlag:
          'is' +
          String(def.key).charAt(0).toUpperCase() +
          String(def.key).slice(1), // flag in the app's 'device_info'
        requiresPoiId: false, // the app needs a POI id (set per terminal in the backoffice) before it can log in
        segments: false, // prints from structured segments (in addition to / instead of HTML)
        normalizeId: function (id) {
          return String(id == null ? '' : id);
        },
      },
      def
    );
    if (!providers[p.key]) order.push(p.key);
    providers[p.key] = p;
    return p;
  }

  function all() {
    return $.map(order, function (k) {
      return providers[k];
    });
  }

  function current() {
    var $b = $('body'),
      list = all();
    for (var i = 0; i < list.length; i++) {
      if ($b.attr(list[i].bodyAttr) === '1') return list[i];
    }
    return null;
  }

  function find(field, value) {
    var list = all();
    for (var i = 0; i < list.length; i++) {
      if (list[i][field] === value) return list[i];
    }
    return null;
  }

  function send(message) {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify(message));
      return true;
    }
    return false;
  }

  function printMessage(html, segments, providerKey) {
    var p = (providerKey && providers[providerKey]) || current();
    if (!p) return null;
    var body = {
      document: html || '',
      contentTypeString: 'HTML',
      receiptTypeString: 'DOCUMENT',
      deliveryMethodString: 'PRINT',
      deliveryAddress: '',
    };
    if (p.segments) body.segments = $.isArray(segments) ? segments : [];
    return { action: p.key, data: { action: 'print', data: body } };
  }

  var OnDevice = {
    // Start a payment / refund in this device's payment app and remember it in OnDeviceTx (see
    // OnDeviceTransaction.js): `message` is the server's payment_data / refund_data. opts.txId is set when
    // another POS asked for it (SocketHub); without it this device's own POS page sells (local).
    start: function (message, opts) {
      if (window.OnDeviceTx) window.OnDeviceTx.begin(message, opts);
      return send(message);
    },
    register: register,
    providers: all,
    current: current,
    active: function () {
      return !!current();
    },
    key: function () {
      var p = current();
      return p ? p.key : null;
    },
    byKey: function (k) {
      return providers[k] || null;
    },
    byPaymentMethod: function (pm) {
      return find('paymentMethod', pm);
    },
    // the provider an app's 'device_info' message says it is (flags isVerifone / isPax / ...)
    fromDeviceInfo: function (info) {
      var list = all();
      for (var i = 0; info && i < list.length; i++) {
        if (info[list[i].deviceFlag]) return list[i];
      }
      return null;
    },
    byPrinterType: function (t) {
      return find('printerType', t);
    },
    isPaymentMethod: function (pm) {
      return !!find('paymentMethod', pm);
    },
    paymentMethods: function () {
      return $.map(all(), function (p) {
        return p.paymentMethod;
      });
    },
    methodSelector: function (prefix) {
      return $.map(all(), function (p) {
        return (
          (prefix || '') + '[data-payment-method="' + p.paymentMethod + '"]'
        );
      }).join(', ');
    },
    uniqueId: function () {
      try {
        return localStorage.getItem(UNIQUE_ID_KEY) || '';
      } catch (e) {
        return '';
      }
    },
    setUniqueId: function (id) {
      try {
        localStorage.setItem(UNIQUE_ID_KEY, id || '');
      } catch (e) {}
    },
    sessionStarted: function () {
      return $('body').attr('is-verifone-session-started') === '1';
    },
    send: send,
    command: function (action, data, providerKey) {
      var p = (providerKey && providers[providerKey]) || current();
      if (!p) return false;
      return send({ action: p.key, data: { action: action, data: data } });
    },
    checkState: function () {
      var p = current();
      if (!p || $('body').attr('data-is-logged-in') !== '1') return false;
      return send({ action: p.key, data: { action: 'check_state' } });
    },
    printMessage: printMessage,
    print: function (html, segments, providerKey) {
      var m = printMessage(html, segments, providerKey);
      return m ? send(m) : false;
    },
  };

  // ---- the solutions we support ---------------------------------------------------------------------
  register({
    key: 'verifone',
    terminalType: 2,
    normalizeId: function (id) {
      return String(id == null ? '' : id).replace(/-/g, '');
    },
  });
  register({
    key: 'pax',
    terminalType: 3,
    segments: true,
    requiresPoiId: true,
  });

  window.OnDevice = OnDevice;
})(window, jQuery);
